import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import ParticleField from "./ParticleField";
import { scrollToSection } from "../lib/smooth";

const EASE = [0.33, 1, 0.68, 1];

const MaskedLine = ({ children, delay, started, className = "" }) => (
  <div className="overflow-hidden">
    <motion.div
      initial={{ y: "115%" }}
      animate={started ? { y: "0%" } : {}}
      transition={{ duration: 1.1, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  </div>
);

const Hero = ({ started, onContact }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 18 });
  const sy = useSpring(my, { stiffness: 40, damping: 18 });
  const fieldX = useTransform(sx, (v) => v * 26);
  const fieldY = useTransform(sy, (v) => v * 26);
  const gridX = useTransform(sx, (v) => v * -12);
  const gridY = useTransform(sy, (v) => v * -12);

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative flex min-h-screen flex-col justify-center overflow-hidden bg-abyss"
    >
      <motion.div style={{ x: gridX, y: gridY }} className="absolute -inset-10 bg-blueprint opacity-70" />
      <motion.div style={{ x: fieldX, y: fieldY }} className="absolute -inset-10 opacity-60">
        <ParticleField />
      </motion.div>
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[70vh] w-[70vw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(0,240,255,0.06),transparent_65%)]" />

      <div className="pointer-events-none absolute left-6 top-24 hidden font-mono text-[10px] uppercase tracking-[0.3em] text-white/30 md:left-12 md:block">
        SYS.STATUS: <span className="text-neon">ONLINE</span>
        <span className="cursor-blink text-cyber">_</span>
      </div>
      <div className="pointer-events-none absolute right-6 top-24 hidden text-right font-mono text-[10px] uppercase tracking-[0.3em] text-white/30 md:right-12 md:block">
        NODE.FFG-01 // EST.2026
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1600px] px-6 pb-28 pt-36 md:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={started ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="mb-8 font-mono text-xs uppercase tracking-[0.35em] text-neon md:text-sm"
          data-testid="hero-overline"
        >
          // Diversified Innovation Hub
        </motion.p>

        <h1
          className="font-display text-[13vw] font-bold uppercase leading-[0.92] tracking-tighter sm:text-[10vw] lg:text-[7.2rem]"
          data-testid="hero-headline"
        >
          <MaskedLine started={started} delay={0.45}>
            <span className="block">Engineering</span>
          </MaskedLine>
          <MaskedLine started={started} delay={0.58}>
            <span className="text-outline-cyan block">the Infrastructure</span>
          </MaskedLine>
          <MaskedLine started={started} delay={0.71}>
            <span className="block">
              of Tomorrow<span className="text-cyber">.</span>
            </span>
          </MaskedLine>
        </h1>

        <div className="mt-14 grid grid-cols-1 gap-12 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={started ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
            className="lg:col-span-5"
          >
            <p
              className="max-w-md text-sm leading-relaxed text-secondary md:text-base"
              data-testid="hero-subheadline"
            >
              Future Factory Group is a diversified innovation hub building
              scalable solutions across technology, advanced manufacturing,
              clean energy, sustainable agriculture, and intelligent real
              estate.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <button
                data-testid="hero-explore-button"
                onClick={() => scrollToSection("#sectors")}
                className="group relative border border-cyber bg-cyber/10 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-cyber transition-all duration-300 hover:bg-cyber hover:text-[#0B0C10] hover:drop-shadow-[0_0_16px_rgba(0,240,255,0.6)]"
              >
                Explore Our Ecosystem
              </button>
              <button
                data-testid="hero-partner-link"
                onClick={onContact}
                className="group flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/70 transition-colors duration-300 hover:text-neon"
              >
                Partner With Us
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
              </button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={started ? { opacity: 1 } : {}}
            transition={{ duration: 1, delay: 1.25 }}
            className="hidden items-end justify-end gap-10 font-mono text-[10px] uppercase tracking-[0.25em] text-white/35 lg:col-span-7 lg:flex"
          >
            <div data-testid="hero-stat-sectors">
              <span className="block text-3xl font-medium text-white/80">05</span>
              Core Divisions
            </div>
            <div data-testid="hero-stat-markets">
              <span className="block text-3xl font-medium text-white/80">GLB</span>
              Borderless Reach
            </div>
            <div data-testid="hero-stat-horizon">
              <span className="block text-3xl font-medium text-neon">∞</span>
              Build Horizon
            </div>
          </motion.div>
        </div>
      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={started ? { opacity: 1 } : {}}
        transition={{ duration: 1, delay: 1.6 }}
        onClick={() => scrollToSection("#vision")}
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3"
        data-testid="hero-scroll-indicator"
        aria-label="Scroll to vision"
      >
        <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-white/40">Scroll</span>
        <span className="h-12 w-px overflow-hidden bg-white/15">
          <motion.span
            className="block h-4 w-px bg-cyber"
            animate={{ y: [0, 48] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          />
        </span>
      </motion.button>
    </section>
  );
};

export default Hero;
